import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import SportsEsportsOutlinedIcon from "@mui/icons-material/SportsEsportsOutlined";

import PublicPageLayout from "./PublicPageLayout";
import { getPublicSiteSx } from "./publicSiteStyles";

const steps = [
  {
    title: "Join with Steam",
    body:
      "Ouro starts with read-only Steam sign-in so players can join quickly without granting inventory, trade, or market permissions. Google remains available as a fallback.",
    icon: <VerifiedUserOutlinedIcon />,
  },
  {
    title: "Complete verification and setup",
    body:
      "Players fill in the competitive details Ouro relies on, pass verification, and configure their account so the system can build better matchmaking context around them.",
    icon: <SettingsSuggestOutlinedIcon />,
  },
  {
    title: "Coordinate regional queue windows",
    body:
      "While the founding player base grows, regional queues are coordinated around evening windows, currently about 6-10pm. Those windows expand as density becomes healthier.",
    icon: <SportsEsportsOutlinedIcon />,
  },
];

export default function HowItWorksPage() {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);

  return (
    <PublicPageLayout
      eyebrow="How it works"
      title="From founding signup to healthier queues."
      description="Join with Steam, complete verification, configure your competitive profile, and help your region build the density needed for cleaner CS2 matchmaking."
      mapBackground
    >
      <Box sx={sx.sectionWithMapBg1}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Three moves before the first match.</Typography>
        </Box>
        <Box sx={sx.stepGrid}>
          {steps.map((step, index) => (
            <Box key={step.title} sx={sx.stepCard}>
              <Box sx={sx.stepHeader}>
                <Box sx={sx.stepIconWrap}>{step.icon}</Box>
                <Typography component="span" sx={sx.stepNumberInline}>
                  {index + 1}
                </Typography>
              </Box>
              <Typography sx={sx.stepTitle}>{step.title}</Typography>
              <Typography sx={sx.stepBody}>{step.body}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>What opens up after verification</Typography>
        </Box>
        <Box sx={{ ...sx.cardGrid, gridTemplateColumns: { xs: "1fr", lg: "1.1fr 0.9fr" } }}>
          <Box sx={sx.card}>
            <Box component="ul" sx={sx.bulletList}>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Ouro can import competitive context like FaceIT-related details during verification.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Players build a long-term reputation profile instead of being treated like disposable accounts.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Membership tiers unlock increasingly fine control over maps, locations, and queue behavior.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Reports become part of the accountability system instead of a throwaway formality.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Every match contributes to your Impact Rating — a single number that reflects what you actually did, not just kills.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Women players can opt in to a soft preference for playing with more women — opt-in only, never a hard filter, and it doesn't change anything for men's queues.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Regional queue windows help early players find each other while the player base grows.
                </Typography>
              </li>
            </Box>
          </Box>
          <Box sx={sx.accentPanel}>
            <Typography sx={sx.cardTitle}>Who this flow is for</Typography>
            <Typography sx={sx.cardBody}>
              Ouro is not trying to hide that it is early. It is for players who
              believe cleaner CS2 matchmaking is worth building, and who are
              willing to help seed their region so better queues can exist.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
              <Button
                component={Link}
                to="/anti-cheat"
                variant="contained"
                sx={sx.primaryButton}
              >
                See anti-cheat
              </Button>
              <Button
                component={Link}
                to="/trust-and-safety"
                variant="outlined"
                sx={sx.secondaryButton}
              >
                Trust and safety
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </PublicPageLayout>
  );
}
